import { displaysZeroPaddedMrn } from "@/configuration/mrn-display";
import { isMrnPrimary, normalizeIdentifiers, trustsColumnLayout } from "@/configuration/mrn-primary";
import { getGroupConfigSubdomain } from "@/configuration/paths";
import { MissingMrnError } from "@/src/lib/error";
import { logAndThrow } from "@/src/lib/logging/logger";
import { stats } from "@/src/lib/logging/stats.server";
import { hasValue } from "@/src/lib/utils/object-utils";
import type { Group } from "@/src/models/group";
import type { Patient } from "@/src/models/patient";
import type { Practitioner } from "@/src/models/practitioner";

type EhrIdKind = "ehrId" | "mrn" | "unknown";

export function classifyEhrId(value: string | null): EhrIdKind {
  if (!value?.trim()) return "unknown";
  // A numeric string of 12 or fewer digits is an MRN. NextGen (InSight) MRNs
  // are 12 digits; ECW MRNs are shorter. Known EHR IDs (NextGen person UUIDs,
  // ECW FHIR base64) always contain non-digit characters.
  if (/^\d{1,12}$/.test(value)) return "mrn";

  return "ehrId";
}

// eCW FHIR resource IDs are opaque 43-character tokens. This is deliberately a
// positive FHIR-ID check, not an MRN classifier: eCW account numbers can be
// numeric, alphabetic, or alphanumeric, so their character shape is not a safe
// way to infer identifier meaning.
function isEcwFhirResourceId(value: string | null): boolean {
  return value != null && /^[A-Za-z0-9._-]{43}$/.test(value);
}

export type EhrIdentifiers = { mrn: string | null; ehrSystemId: string | null };

export type EhrIdSlots = Pick<Patient, "ehrId" | "secondaryEhrId"> | Pick<Practitioner, "ehrId" | "secondaryEhrId">;

// With `group`, use its column convention (see `mrn-primary.ts`). An eCW FHIR resource ID in the MRN
// slot is always recovered as the EHR ID; groups that trust their column layout (see `trustsColumnLayout`) stop there because their data is normalized,
// while other MRN-primary groups also keep legacy numeric-shape recovery for scrambled rows. Without
// `group`, fall back to structural classification, which cannot distinguish every EHR.
export function resolveEhrIdentifiers(slots: EhrIdSlots, group?: Group | null): EhrIdentifiers {
  const ehrIdCol = slots.ehrId?.trim() || null;
  const secondaryCol = slots.secondaryEhrId?.trim() || null;

  if (group) {
    if (isMrnPrimary(group)) {
      const subdomain = getGroupConfigSubdomain(group);
      const trustColumnLayout = trustsColumnLayout(group);
      const mrnSlotContainsEcwFhirId = isEcwFhirResourceId(ehrIdCol);
      const structurallySwapped =
        !trustColumnLayout && classifyEhrId(ehrIdCol) === "ehrId" && classifyEhrId(secondaryCol) === "mrn";

      // Two recognizable FHIR IDs is the historical double-FHIR corruption,
      // not evidence that either value is an MRN. Prefer the configured EHR-ID
      // slot and fail closed on the missing MRN.
      if (mrnSlotContainsEcwFhirId && isEcwFhirResourceId(secondaryCol)) {
        return { ehrSystemId: secondaryCol, mrn: null };
      }

      if (mrnSlotContainsEcwFhirId || structurallySwapped) {
        stats.counter("ehr.patient.swapped_ehr_id_columns", 1, {
          tags: { subdomain },
        });

        return { ehrSystemId: ehrIdCol, mrn: secondaryCol };
      }

      return { ehrSystemId: secondaryCol, mrn: ehrIdCol };
    }

    return { ehrSystemId: ehrIdCol, mrn: secondaryCol };
  }

  const uniqueCandidates = Array.from(new Set([ehrIdCol, secondaryCol].filter((c): c is string => Boolean(c))));

  if (uniqueCandidates.length === 0) return { ehrSystemId: null, mrn: null };

  // If there's no ambiguity (only one unique value), it's the primary ID.
  // For single-ID EHRs (Athena, Mod-Med), this numeric string is the "EHR ID".
  if (uniqueCandidates.length === 1) {
    const val = uniqueCandidates[0];
    const isMrn = classifyEhrId(val) === "mrn";

    return { ehrSystemId: val, mrn: isMrn ? val : null };
  }

  // Contention: two different values. Use structural classification to assign slots.
  const ehrSystemId = uniqueCandidates.find((c) => classifyEhrId(c) === "ehrId") ?? null;
  const mrn = uniqueCandidates.find((c) => classifyEhrId(c) === "mrn") ?? null;

  return { ehrSystemId, mrn };
}

export function tryGetEhrSystemId(slots: EhrIdSlots, group?: Group | null): string | null {
  return resolveEhrIdentifiers(slots, group).ehrSystemId;
}

/**
 * The identifier to show staff (UI cells, cards, CSV exports): the MRN without its leading zeros
 * (kept for groups whose EHR shows them, see `mrn-display.ts`), falling back to the EHR's own id only
 * when that id is one a human can use. An eCW FHIR resource id is opaque plumbing, so a row with no
 * MRN renders empty rather than printing the token in a field staff read as the MRN.
 */
function toDisplayIdentifiers({ mrn, ehrSystemId }: EhrIdentifiers, group?: Group | null): EhrIdentifiers {
  const displayedMrn = group && displaysZeroPaddedMrn(group) ? mrn : mrn?.replace(/^0+/, "");
  // Dropped from either slot: whichever column convention a group uses, the token can land in the one
  // the resolver reports as the MRN, and neither slot makes it a value staff can act on.
  const usableMrn = isEcwFhirResourceId(mrn) ? null : displayedMrn || null;
  const usableEhrSystemId = isEcwFhirResourceId(ehrSystemId) ? null : ehrSystemId;

  return { mrn: usableMrn, ehrSystemId: usableEhrSystemId };
}

export function displayEhrIdentifiers(slots: EhrIdSlots, group?: Group | null): EhrIdentifiers {
  return toDisplayIdentifiers(resolveEhrIdentifiers(slots, group), group);
}

export function displayEhrRecordIdentifiers(
  record: Pick<Patient, "ehrId" | "secondaryEhrId">,
  group?: Group | null
): EhrIdentifiers {
  return toDisplayIdentifiers(ehrRecordIdentifiers(record), group);
}

export function displayEhrId(slots: EhrIdSlots, group?: Group | null): string | null {
  const { mrn, ehrSystemId } = displayEhrIdentifiers(slots, group);

  return mrn || ehrSystemId;
}

export function tryGetMrn(slots: EhrIdSlots, group?: Group | null): string | null {
  return resolveEhrIdentifiers(slots, group).mrn;
}

// Ids from a patient just fetched from the EHR: ehrId is the system id, secondaryEhrId the MRN (no per-group column swap, unlike stored rows).
export function ehrRecordIdentifiers(record: Pick<Patient, "ehrId" | "secondaryEhrId">): EhrIdentifiers {
  return { ehrSystemId: record.ehrId?.trim() || null, mrn: record.secondaryEhrId?.trim() || null };
}

/**
 * Whether two resolved identifier sets prove the same EHR chart: they share an EHR system ID or MRN and
 * conflict on neither. Missing identifiers never prove identity. Resolve each side by its origin first:
 * `resolveEhrIdentifiers(row, group)` for stored rows, `ehrRecordIdentifiers(record)` for fresh EHR records.
 */
export function isSameEhrIdentity(left: EhrIdentifiers, right: EhrIdentifiers): boolean {
  const ehrSystemIdsAgree = left.ehrSystemId && right.ehrSystemId ? left.ehrSystemId === right.ehrSystemId : null;
  const mrnsAgree = left.mrn && right.mrn ? left.mrn === right.mrn : null;

  return ehrSystemIdsAgree !== false && mrnsAgree !== false && (ehrSystemIdsAgree === true || mrnsAgree === true);
}

export function requireEhrSystemId(patient: Patient, group?: Group | null): string {
  const ehrId = tryGetEhrSystemId(patient, group);

  return hasValue(ehrId) && ehrId ? ehrId : logAndThrow("Patient EHR ID not found", { patientId: patient.id });
}

/**
 * Returns the medical record number from `ehrId` / `secondaryEhrId`, using the group's column
 * convention when `group` is passed (see `resolveEhrIdentifiers`). Throws if no MRN is present.
 *
 * - **Patient** — MRN sent to the EHR for scheduling, document upload, and patient lookup
 *   (e.g. Cobalt `patient_mrn`, CloudCruise workflows). Prefer passing `group` so MRN vs EHR ID
 *   is not guessed from digit shape alone.
 * - **Practitioner** — provider identifier stored in the same two columns (e.g. Cobalt scheduling
 *   requires an MRN on both patient and practitioner before availability/booking calls).
 */
export function requireMrn(patient: Patient, group?: Group | null): string;
export function requireMrn(practitioner: Practitioner, group?: Group | null): string;
export function requireMrn(entity: Patient | Practitioner, group?: Group | null): string {
  const mrn = tryGetMrn(entity, group);

  if (hasValue(mrn) && mrn) {
    return mrn;
  }

  // Typed error (not logAndThrow's bare Error) so agent endpoints can surface 422/missing_data
  // instead of 500/http_5xx. The boundary handler logs it; the id rides on meta. See VAL-2006.
  if ("role" in entity) {
    throw new MissingMrnError("Practitioner MRN not found", { practitionerId: entity.id });
  }

  throw new MissingMrnError("Patient MRN not found", { patientId: entity.id });
}

export function tryAnyEhrId(patient: Patient): string | null {
  return patient.ehrId?.trim() || patient.secondaryEhrId?.trim() || null;
}

export function requireAnyEhrId(patient: Patient): string {
  const ehrId = tryAnyEhrId(patient);

  return hasValue(ehrId) && ehrId ? ehrId : logAndThrow("Patient EHR ID not found", { patientId: patient.id });
}

// A "MRN" equal to the EHR system id is the eCW double-FHIR-id corruption (the FHIR resource id in
// the MRN slot), never a real MRN. Applied to both the incoming record and the stored row: because
// `tryGetMrn` trusts column position for MRN-primary groups, an already-corrupted stored row would
// otherwise report its FHIR id as the MRN — tripping a false conflict and blocking repair.
function realMrnOrNull(mrn: string | null, ehrSystemId: string | null): string | null {
  return mrn && mrn !== ehrSystemId ? mrn : null;
}

export type MergedEhrIdentifiers =
  | {
      conflict: true;
      incomingEhrId: string | null;
      existingEhrId: string | null;
      incomingMrn: string | null;
      existingMrn: string | null;
    }
  | { conflict: false; ehrId: string | null; secondaryEhrId: string | null };

/**
 * Merges EHR identifiers when updating an already-identified patient, honoring the group's column
 * convention. `incoming` is read as EHR-record layout (ehrId = system id, secondaryEhrId = MRN) — how
 * a record arrives from an EHR/FHIR patient search; `existing` is a stored row that follows the
 * group's column convention. Returns the columns to write, or a `conflict` signal (caller should skip
 * the update, leaving the existing row untouched) when incoming identifiers disagree with existing
 * non-null ones.
 *
 * Both sides drop a "MRN" equal to their EHR system id — the eCW double-FHIR-id corruption (the FHIR
 * resource id sitting in the MRN slot), never a real MRN. Guarding the stored row matters too:
 * `tryGetMrn` trusts column position for MRN-primary groups, so an already-corrupted row would report
 * its FHIR id as the MRN, tripping a false conflict and blocking a healthy incoming record from
 * repairing it.
 *
 * An incoming record with no system id (e.g. Cobalt, which exposes an MRN and no FHIR id) contributes
 * only its MRN; the stored EHR link is carried through untouched.
 */
export function mergeEhrIdentifiers(
  incoming: Pick<Patient, "ehrId" | "secondaryEhrId">,
  existing: Pick<Patient, "ehrId" | "secondaryEhrId">,
  group: Group
): MergedEhrIdentifiers {
  const incomingIds = ehrRecordIdentifiers(incoming);
  const incomingEhrId = incomingIds.ehrSystemId;
  const incomingMrn = realMrnOrNull(incomingIds.mrn, incomingEhrId);
  const existingEhrId = tryGetEhrSystemId(existing, group);
  const existingMrn = realMrnOrNull(tryGetMrn(existing, group), existingEhrId);

  const ehrIdConflict = Boolean(incomingEhrId && existingEhrId && incomingEhrId !== existingEhrId);
  const mrnConflict = Boolean(incomingMrn && existingMrn && incomingMrn !== existingMrn);

  if (ehrIdConflict || mrnConflict) {
    return { conflict: true, incomingEhrId, existingEhrId, incomingMrn, existingMrn };
  }

  return {
    conflict: false,
    ...normalizeIdentifiers(group, incomingEhrId ?? existingEhrId, incomingMrn ?? existingMrn),
  };
}
